import { Injectable, inject } from "@angular/core";
import { MatBottomSheet, MatBottomSheetConfig, MatBottomSheetRef } from "@angular/material/bottom-sheet";
import { from, mergeMap } from "rxjs";
import { ModalAudio, ModalParms } from "@app/shared/models/modal.model";
import { BreakpointService } from "@app/core/services/breakpoint.service";
import { FileDownload } from "@app/shared/models/file.model";
import { MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { DocumentsPlusAttachParms } from "@app/app-modules/documents-plus/documents-plus.model";

@Injectable({
  providedIn: "root"
})
export class ModalSheetService {
  private dialog = inject(MatDialog);
  private sheet = inject(MatBottomSheet);
  private bps = inject(BreakpointService);

  /**
   * Open a modal passing in a component and configuration settings
   * @param component
   * @param config
   * @param forceSheet
   */
  open(component: any, config: MatDialogConfig | MatBottomSheetConfig, forceSheet?: boolean, enableClickClose?: boolean) {
    config.autoFocus = false;
    config.disableClose = enableClickClose ? false : true;
    // NOTE: Only 1 sheet can be open at a time. By default opening another will close the previous.
    // In this instance, we will show a dialog over the sheet if not forced.
    if (config instanceof MatDialogConfig || (this.sheetRef && !forceSheet)) {
      const c: MatDialogConfig = config as MatDialogConfig;
      c.panelClass = this.modalsOpened >= 1 ? "modal__show-box-shadow" : "";

      return this.dialog.open(component, c).afterClosed();
    } else {
      config.panelClass = "bottom-sheet-container__full-width";
      return this.sheet.open(component, config).afterDismissed();
    }
  }

  /**
   * Opens a modal and returns the reference to the modal to allow different type of subscriptions.
   * @param component
   * @param config
   */
  openRef(component: any, config: MatDialogConfig | MatBottomSheetConfig) {
    config.autoFocus = false;
    config.disableClose = true;
    let dialogRef: any;
    if (config instanceof MatDialogConfig) {
      dialogRef = this.dialog.open(component, config);
    } else {
      dialogRef = this.sheet.open(component, config);
    }
    return dialogRef;
  }

  openDirtyModal(data: ModalParms) {
    const dialogConfig = this.isDesktop ? new MatDialogConfig() : new MatBottomSheetConfig();
    dialogConfig.data = data;
    // Settings this property to false, allows the modal to stay open when the canDeactivate() guard fires.
    // This was a issue that was discovered in firefox.
    dialogConfig.closeOnNavigation = false;

    return from(
      import("@shared/components/modals/dirty-form-modal/dirty-form-modal.component").then(({ DirtyFormComponent }) => {
        return DirtyFormComponent;
      })
    ).pipe(
      mergeMap((component) => {
        return this.open(component, dialogConfig);
      })
    );
  }

  /**
   * Opens a caution modal
   * For parms pass in the translation constant
   * @param title : the title of the dialog
   * @param body  : what the body will say
   */
  openCautionModal(data: ModalParms) {
    const dialogConfig = this.isDesktop ? new MatDialogConfig() : new MatBottomSheetConfig();
    dialogConfig.data = data;

    return from(
      import("@app/shared/components/modals/caution-modal/caution-modal.component").then(({ CautionModalComponent }) => {
        return CautionModalComponent;
      })
    ).pipe(
      mergeMap((component) => {
        return this.open(component, dialogConfig);
      })
    );
  }

  /**
   * Opens a warning modal
   * For parms except id, pass in the translation constant
   * @param title : the title of the dialog
   * @param body  : what the body will say
   * @param button  : show Yes button
   */
  openWarningModal(title: string, body: string, button: boolean = true) {
    const dialogConfig = this.isDesktop ? new MatDialogConfig() : new MatBottomSheetConfig();
    dialogConfig.data = { title: title, body: body, button: button };
    return from(
      import("@app/shared/components/modals/warning-modal/warning-modal.component").then(({ WarningModalComponent }) => {
        return WarningModalComponent;
      })
    ).pipe(
      mergeMap((component) => {
        return this.open(component, dialogConfig);
      })
    );
  }

  /**
   * Open loading modal
   * @returns
   */
  openLoadingModal(data: string) {
    const dialogConfig = this.isDesktop ? new MatDialogConfig() : new MatBottomSheetConfig();
    dialogConfig.data = data;
    return from(
      import("@shared/components/modals/loading-modal/loading-modal.component").then(({ LoadingModalComponent }) => {
        return LoadingModalComponent;
      })
    ).pipe(
      mergeMap((component) => {
        return this.open(component, dialogConfig);
      })
    );
  }

  /**
   * Opens audio modal
   * @param fileDownloadParms
   * @param fileName
   */
  openAudioModal(fileDownloadParms: FileDownload, fileName: string) {
    const dialogConfig = this.isDesktop ? new MatDialogConfig() : new MatBottomSheetConfig();
    dialogConfig.data = {
      fileParms: fileDownloadParms,
      fileName: fileName
    } as ModalAudio;
    return from(
      import("@shared/components/modals/audio-modal/audio-modal.component").then(({ AudioModalComponent }) => {
        return AudioModalComponent;
      })
    ).pipe(
      mergeMap((component) => {
        return this.open(component, dialogConfig);
      })
    );
  }

  /**
   * Opens a login content modal
   */
  openLoginModal(config: MatDialogConfig | MatBottomSheetConfig) {
    return from(
      import("@shared/components/modals/login-modal/login-modal.component").then(({ LoginModalComponent }) => {
        return LoginModalComponent;
      })
    ).pipe(
      mergeMap((component) => {
        return this.open(component, config);
      })
    );
  }

  /**
   * Opens a content modal
   * For parms pass in the translation constant
   * @param title : the title of the dialog
   * @param body  : what the body will say
   */
  openContent(title: string, body: string, forceSheet?: boolean, button?: string, isImage?: boolean) {
    const dialogConfig = this.isDesktop ? new MatDialogConfig() : new MatBottomSheetConfig();
    dialogConfig.data = {
      title: title,
      body: body,
      button: button,
      isImage: isImage
    } as ModalParms;
    return from(
      import("@shared/components/modals/content-modal/content-modal.component").then(({ ContentModalComponent }) => {
        return ContentModalComponent;
      })
    ).pipe(
      mergeMap((component) => {
        return this.open(component, dialogConfig, forceSheet);
      })
    );
  }

  /**
   * Opens the attach documents modal
   * @param company
   * @param index
   * @param documentType
   */
  openAttachDocuments(applicationId?: string, company?: string, index?: string, documentType?: string, isWebFace?: boolean) {
    const dialogConfig = this.isDesktop ? new MatDialogConfig() : new MatBottomSheetConfig();
    dialogConfig.data = {
      applicationId,
      company,
      index,
      documentType,
      isWebFace
    } as DocumentsPlusAttachParms;

    return from(
      import("@app/app-modules/documents-plus/documents-plus-attach-modal/documents-plus-attach-modal.component").then(
        ({ DocumentsPlusAttachModalComponent }) => {
          return DocumentsPlusAttachModalComponent;
        }
      )
    ).pipe(
      mergeMap((component) => {
        return this.open(component, dialogConfig);
      })
    );
  }

  /**
   * Dialog will appear once a user is inactive for a specified amount of time.
   */
  openTimeout() {
    const dialogConfig = this.isDesktop ? new MatDialogConfig() : new MatBottomSheetConfig();
    return from(
      import("@shared/components/modals/timeout-modal/timeout-modal.component").then(({ TimeoutModalComponent }) => {
        return TimeoutModalComponent;
      })
    ).pipe(
      mergeMap((component) => {
        return this.open(component, dialogConfig);
      })
    );
  }

  /**
   * Used to close the opened sheet on a swipe down event mobile only.
   */
  onSwipeDown(data?: any) {
    if (this.sheetRef?.instance) {
      this.matSheet.dismiss(data);
    }
  }

  closeAll() {
    this.modalsOpened ? this.matDialog.closeAll() : this.matSheet.dismiss();
  }

  closeByRef(ref: MatDialogRef<any> | MatBottomSheetRef<any>) {
    ref instanceof MatDialogRef ? this.matDialog.closeAll() : this.matSheet.dismiss();
  }

  get matDialog(): MatDialog {
    return this.dialog;
  }

  get matSheet(): MatBottomSheet {
    return this.sheet;
  }

  get modalsOpened() {
    return this.dialog.openDialogs.length;
  }

  get sheetRef(): MatBottomSheetRef {
    return this.sheet._openedBottomSheetRef;
  }

  /**
   * @returns if a sheet or modal is opened
   */
  get isOpen(): boolean {
    return this.modalsOpened > 0 || !!this.sheetRef;
  }

  get isDesktop(): boolean {
    return !this.bps.isMobile();
  }
}
